import * as React from "react";

import { cn } from "utils/cn";
import { formatCount } from "utils/format";

// Chart styling shared by every chart (Analytics, Billing). Colors are the kit's chart tokens, so the charts
// follow the light and dark themes without per-theme values.
export const CHART_COLORS = {
  1: "rgb(var(--chart-1))",
  2: "rgb(var(--chart-2))",
  3: "rgb(var(--chart-3))",
  4: "rgb(var(--chart-4))",
  5: "rgb(var(--chart-5))",
  6: "rgb(var(--chart-6))",
} as const;

export const GRID_STROKE = "rgb(var(--border))";
export const AXIS_TICK = { fontSize: 12, fill: "rgb(var(--muted-foreground))" };
export const AXIS_LINE = { stroke: "rgb(var(--border))" };
export const CURSOR_FILL = { fill: "rgb(var(--surface-muted))" };
export const CURSOR_LINE = { stroke: "rgb(var(--border))", strokeWidth: 1 };

interface TooltipEntry {
  name?: string | number;
  value?: number | string | Array<number | string>;
  color?: string;
  payload?: { fill?: string };
}

/** Props Recharts injects into a custom `content` element, plus our formatters. */
export interface ChartTooltipProps {
  active?: boolean;
  label?: string | number;
  payload?: TooltipEntry[];
  formatLabel?: (label: string) => string;
  formatValue?: (value: number) => string;
}

/** Tooltip body for Recharts' `<Tooltip content={<ChartTooltip />} />`. */
export const ChartTooltip: React.FC<ChartTooltipProps> = ({
  active,
  label,
  payload,
  formatLabel,
  formatValue = formatCount,
}) => {
  if (!active || !payload || payload.length === 0) {
    return null;
  }
  const hasLabel = label !== undefined && label !== null && label !== "";
  const title = hasLabel ? (formatLabel ? formatLabel(String(label)) : String(label)) : null;
  return (
    <div className="min-w-[9rem] rounded-card border border-border bg-surface-raised px-3 py-2 text-sm text-foreground shadow-popover">
      {title && <p className="mb-1 text-xs text-muted-foreground">{title}</p>}
      <ul className="space-y-1">
        {payload.map((entry, index) => (
          <li key={`${entry.name ?? "value"}-${index}`} className="flex items-center gap-2">
            <span
              aria-hidden="true"
              className="h-2 w-2 shrink-0 rounded-full"
              style={{ background: entry.color || entry.payload?.fill }}
            />
            <span className="text-muted-foreground">{entry.name}</span>
            <span className="ml-auto pl-3 font-medium tabular-nums">
              {typeof entry.value === "number" ? formatValue(entry.value) : "—"}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};

export interface ChartFrameProps {
  /** Text alternative read by screen readers in place of the drawing. */
  summary: string;
  className?: string;
  children: React.ReactNode;
}

/** Sized box for a ResponsiveContainer that screen readers announce as one image. */
export const ChartFrame: React.FC<ChartFrameProps> = ({ summary, className, children }) => (
  <div role="img" aria-label={summary} className={cn("h-64 w-full", className)}>
    {children}
  </div>
);

export interface LegendItem {
  label: string;
  color: string;
  value?: React.ReactNode;
}

/** Small swatch legend for charts with more than one series or slice. */
export const ChartLegend: React.FC<{ items: LegendItem[]; className?: string }> = ({
  items,
  className,
}) => (
  <ul className={cn("space-y-2 text-sm", className)}>
    {items.map((item) => (
      <li key={item.label} className="flex items-center gap-2.5">
        <span
          aria-hidden="true"
          className="h-2.5 w-2.5 shrink-0 rounded-full"
          style={{ background: item.color }}
        />
        <span className="text-muted-foreground">{item.label}</span>
        {item.value !== undefined && (
          <span className="ml-auto pl-4 font-medium tabular-nums text-foreground">
            {item.value}
          </span>
        )}
      </li>
    ))}
  </ul>
);
